import { InputRef, message } from "antd";
import {
  deleteComment,
  postComment,
  resolveComment,
  updateComment,
} from "api/comments";
import classNames from "classnames";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useClickAway } from "react-use";
import { PostCommentData } from "store/posts/postConstants";
import { UserInfo } from "store/user/userConstants";
import PostCommentDisplay from "./PostCommentDisplay";
import PostCommentInput from "./PostCommentInput";
import { getCommentMenuItems, getTimeAgo } from "./postCommentUtil";

type PostCommentProps = {
  postId: string;
  currentUser: UserInfo;
  readonly?: boolean;
  currentComment?: PostCommentData;
  editingComment: "new" | string | null;
  setEditingComment: (commentId: "new" | string | null) => void;
  refetchComments: () => Promise<unknown>;
};

/**
 * Renders a comment on a post. Handles rendering both an existing comment, or a new comment.
 *
 * @param postId The post that this comment belongs to.
 * @param currentUser Signed in user.
 * @param readonly Show in readonly mode. Hides certain buttons.
 * @param currentComment The existing comment to render.
 * @param editingComment The id of another PostComment within the same container that is being edited, or `new` if the comment
 * being edited does not have an id yet. Used to disable certain buttons when a different comment is being edited.
 * @param setEditingComment Callback to set the `editingComment` within the container.
 * @param refetchComments Refetch the comments for this post.
 */
const PostComment = ({
  postId,
  currentUser,
  readonly,
  currentComment,
  editingComment,
  setEditingComment,
  refetchComments,
}: PostCommentProps) => {
  const [commentBody, setCommentBody] = useState(currentComment?.body ?? "");
  const [showBodyInput, setShowBodyInput] = useState(
    !currentComment && !readonly
  );
  const [loading, setLoading] = useState<
    "comment" | "delete" | "resolve" | false
  >(false);
  const commentTimeAgo = useMemo(
    () => getTimeAgo(currentComment?.created_at),
    [currentComment?.created_at]
  );

  const bodyInputRef = useRef<InputRef>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  // Only run time ago update interval logic for existing comments
  useEffect(() => {
    // Interval logic for time ago updates removed as it's handled by useMemo and component reactivity
  }, []); // Empty dependency array as effect is now empty or might be removed

  useEffect(() => {
    currentComment?.body && setCommentBody(currentComment.body);
  }, [currentComment?.body]);

  // Focus input when it becomes visible, either for a new comment or editing an existing one
  useLayoutEffect(() => {
    if (showBodyInput) {
      setEditingComment(currentComment?.id ?? "new");
      bodyInputRef.current?.focus({ preventScroll: true }); // Prevent page scroll on focus
    } else {
      // If the input is hidden, clear the editing state IF it matches this component
      if (editingComment === (currentComment?.id ?? "new")) {
        setEditingComment(null);
      }
    }
  }, [
    showBodyInput,
    currentComment,
    setEditingComment,
    editingComment,
    bodyInputRef, // Add ref to dependencies
  ]);

  useClickAway(
    inputContainerRef,
    () =>
      currentComment &&
      currentComment.body === commentBody &&
      !loading &&
      setShowBodyInput(false),
    currentComment ? undefined : []
  );

  const saveComment = async (resolve?: boolean | undefined) => {
    try {
      if (currentComment) {
        if (resolve === undefined) {
          setLoading("comment");
          await updateComment({
            id: currentComment.id,
            body: commentBody,
          });
          await refetchComments();
          setShowBodyInput(false);
          setLoading(false);
        } else {
          setLoading("resolve");
          await resolveComment({
            id: currentComment.id,
            resolved: resolve,
          });
          await refetchComments();
          setLoading(false);
        }
      } else {
        setLoading("comment");
        await postComment({ postId, commentBody: commentBody });
        await refetchComments();
        setShowBodyInput(false);
        setCommentBody("");
        setLoading(false);
      }
    } catch (error) {
      message.error("Unable to save comment, please try again");
      setLoading(false);
    }
  };

  const removeComment = async () => {
    if (!currentComment) {
      return;
    }

    setLoading("delete");
    try {
      await deleteComment(currentComment);
      await refetchComments();
    } catch (error) {
      message.error("Unable to delete comment, please try again");
    }
    setLoading(false);
  };

  const handleKeyDown = (key: string, shiftKey: boolean) => {
    switch (key) {
      case "Enter":
        !shiftKey && commentBody.trim() && saveComment();
        break;
      case "Escape":
        setShowBodyInput(false);
        setCommentBody(currentComment?.body ?? "");
        break;
    }
  };

  const CURRENT_COMMENT_MENU = useMemo(
    () =>
      getCommentMenuItems({
        readonly: readonly,
        currentComment: currentComment,
        currentUser: currentUser,
        editingComment: editingComment,
        onEdit: () => setShowBodyInput(true),
        onDelete: removeComment,
      }),
    [readonly, currentComment, currentUser, editingComment, removeComment]
  );

  return (
    <>
      {currentComment || (!readonly && !currentComment) ? (
        <div
          ref={inputContainerRef}
          className={classNames(
            "grid grid-cols-[auto_1fr_auto] gap-y-1 gap-x-2",
            {
              "pr-2": Boolean(currentComment && showBodyInput),
              "items-center": Boolean(currentComment && !showBodyInput),
            }
          )}
        >
          {showBodyInput || (!currentComment && !readonly) ? (
            <PostCommentInput
              currentUser={currentUser}
              commentBody={commentBody}
              setCommentBody={setCommentBody}
              loading={loading === "comment" ? "comment" : false}
              saveComment={() => saveComment()}
              handleKeyDown={handleKeyDown}
              bodyInputRef={bodyInputRef}
            />
          ) : (
            currentComment && (
              <PostCommentDisplay
                currentComment={currentComment}
                commentTimeAgo={commentTimeAgo}
                readonly={readonly}
                loading={
                  loading === "resolve" || loading === "delete"
                    ? loading
                    : false
                }
                saveComment={saveComment}
                CURRENT_COMMENT_MENU={CURRENT_COMMENT_MENU}
              />
            )
          )}
        </div>
      ) : null}
    </>
  );
};

export default PostComment;
